import { inject, isDevMode } from '@angular/core';
import type { Provider } from '@angular/core';
import { SawDust, LogLevel, DatadogService } from '@cudirect/shared-ui-library';
import { LOG_LAYER } from '@shared/loglayer/loglayer.token';
import { createLogLayer } from '@shared/loglayer/loglayer.module';
import { LOG_LAYER_CONFIG } from '@shared/loglayer/loglayer-config.token';
import { serializeError } from 'serialize-error';
import { DataDogBrowserLogsTransport } from '@loglayer/transport-datadog-browser-logs';
import { Logger } from '@datadog/browser-logs';
import { ConsoleTransport } from 'loglayer';

export const provideSawDust = (serviceName: string, environment: string, logLevel: LogLevel = LogLevel.info): Provider[] => [
  {
    provide: LOG_LAYER_CONFIG,
    useFactory: () => {
      const datadogService = inject(DatadogService);

      return {
        errorSerializer: serializeError,
        metadataFieldName: 'metadata',
        transport: [
          isDevMode()
            ? new ConsoleTransport({
                logger: console,
                level: 'debug'
              })
            : new DataDogBrowserLogsTransport({
                logger: {
                  info: (message: string, messageContext?: object, error?: Error): void => {
                    datadogService.info(message, messageContext, error);
                  },
                  warn: (message: string, messageContext?: object, error?: Error): void => {
                    datadogService.info(message, messageContext, error);
                  },
                  error: (message: string, messageContext?: object, error?: Error): void => {
                    datadogService.info(message, messageContext, error);
                  },
                  debug: (message: string, messageContext?: object, error?: Error): void => {
                    datadogService.info(message, messageContext, error);
                  }
                } as Logger
              })
        ]
      };
    },
    deps: [DatadogService]
  },
  { provide: LOG_LAYER, useFactory: createLogLayer, deps: [LOG_LAYER_CONFIG] },
  {
    provide: SawDust,
    useFactory: () => {
      const logLayer = inject(LOG_LAYER);
      const datadogService = inject(DatadogService);

      const sawdust = new SawDust();
      sawdust.init({
        serviceName,
        environment,
        logLevel,
        logLayer,
        datadogService
      });
      sawdust.withContext({
        env: environment,
        source: 'client'
      });
      return sawdust;
    },
    deps: [LOG_LAYER, DatadogService]
  }
];
